import React from "react";
import { Row, Col } from "react-bootstrap";
import { RBSModal } from "../../base/rbs_components/RBSModal.tsx";
import { RBSTextField } from "../../base/rbs_components/RBSTextField.tsx";
import { RBSDropdown } from "../../base/rbs_components/RBSDropdown.tsx";
import type { FeeInput, Fee, WaterfallItemInput, WaterfallItem, Variable } from "../../base/ui_general/GeneralInterfaces.tsx";
import { LabelTextMessageTypes, ValidationSchemes, TextfieldInputTypes } from "../../base/Settings.tsx";
import { getLabelTextMessage } from "../../base/utils/GeneralUtilities.tsx";
import type { InputStateControl } from "../../base/utils/InputStateControl.tsx";
import type { UserLanguages } from "../../base/Settings.tsx";
import { WaterfallItemTypeEnum } from "../../base/Settings.tsx";

/**
 * Props for FeeDetailModal.
 * This interface describes the data structure for displaying and editing a Fee in a modal dialog.
 */
export interface FeeDetailModalProps {
    /** Indicates whether the modal is visible. */
    show: boolean;
    /** The Fee to display or edit (main type, not input). */
    fee?: Fee;
    /** The associated WaterfallItem to display or edit. */
    waterfall_item?: WaterfallItem;
    /** Callback to submit the fee input. */
    onSubmit: (feeInput: FeeInput, waterfallItemInput: WaterfallItemInput) => void;
    /** Callback to close the modal. */
    onClose: () => void;
    /** Indicates if the form input is valid. */
    isValid: boolean;
    /** InputStateControl instance for managing input state and validation. */
    inputStateControl: InputStateControl;
    /** Indicates the preferred user language. */
    user_language: UserLanguages;
    /** Array of available variables for reference_variable_id selection. */
    variables: Array<Variable> | undefined;
}

export const FeeDetailModal: React.FC<FeeDetailModalProps> = (props: FeeDetailModalProps) => {
    // Helper to prefill values from fee or waterfall_item, or use defaults
    const prefill = (key: string): string => {
        if (props.fee && key in props.fee) {
            const value = props.fee[key as keyof typeof props.fee];
            return String(value ?? "");
        }
        if (props.waterfall_item && key in props.waterfall_item) {
            const value = props.waterfall_item[key as keyof typeof props.waterfall_item];
            return String(value ?? "");
        }
        switch (key) {
            case "reference_variable_id":
                return "";
            case "name":
                return "New Fee";
            case "description":
                return "Fee for transaction";
            case "fix_amount":
                return "1000";
            case "variable_rate":
                return "0.001";
            default:
                return "";
        }
    };
    const headerTitle = props.fee
        ? getLabelTextMessage(LabelTextMessageTypes.UPDATE_FEE_TITLE, props.user_language)
        : getLabelTextMessage(LabelTextMessageTypes.NEW_FEE_CREATION_TITLE, props.user_language);
    return (
        <RBSModal
            show={props.show}
            header_title={headerTitle}
            show_close_button
            onClose={props.onClose}
            primary_button_label={getLabelTextMessage(LabelTextMessageTypes.SUBMIT, props.user_language)}
            secondary_button_label={getLabelTextMessage(LabelTextMessageTypes.CANCEL, props.user_language)}
            onClickSecondary={props.onClose}
            primary_button_disabled={!props.isValid}
            size={"xl"}
            onClickPrimary={() => {
                let transactionIdRaw = props.inputStateControl.getStringValueForKey("transaction_id");
                if (!transactionIdRaw) transactionIdRaw = "";
                const referenceVariableIdRaw = props.inputStateControl.getStringValueForKey("reference_variable_id");
                const feeInput: FeeInput = {
                    fix_amount: Number(props.inputStateControl.getStringValueForKey("fix_amount")),
                    variable_rate: Number(props.inputStateControl.getStringValueForKey("variable_rate")),
                    reference_variable_id: referenceVariableIdRaw === "" ? undefined : referenceVariableIdRaw,
                };
                const waterfallItemInput: WaterfallItemInput = {
                    name: props.inputStateControl.getStringValueForKey("name") ?? "",
                    description: props.inputStateControl.getStringValueForKey("description") ?? "",
                    transaction_id: transactionIdRaw,
                    type: WaterfallItemTypeEnum.FEE,
                };
                props.onSubmit(feeInput, waterfallItemInput);
                props.onClose();
            }}
        >
            <Row>
                <Col md={6}>
                    <RBSTextField
                        label={getLabelTextMessage(LabelTextMessageTypes.NAME, props.user_language)}
                        input_key="name"
                        prefill={prefill("name")}
                        validation_schemes={[ValidationSchemes.REQUIRED]}
                        input_state_control={props.inputStateControl}
                        type={TextfieldInputTypes.TEXT}
                    />
                </Col>
                <Col md={6}>
                    <RBSTextField
                        label={getLabelTextMessage(LabelTextMessageTypes.FIXED_AMOUNT, props.user_language)}
                        input_key="fix_amount"
                        prefill={prefill("fix_amount")}
                        validation_schemes={[ValidationSchemes.REQUIRED, ValidationSchemes.NUMBER, ValidationSchemes.NON_NEGATIVE]}
                        input_state_control={props.inputStateControl}
                        type={TextfieldInputTypes.NUMBER}
                    />
                </Col>
            </Row>
            <Row>
                <Col md={6}>
                    <RBSTextField
                        label={getLabelTextMessage(LabelTextMessageTypes.VARIABLE_RATE, props.user_language)}
                        input_key="variable_rate"
                        prefill={prefill("variable_rate")}
                        validation_schemes={[ValidationSchemes.REQUIRED, ValidationSchemes.NUMBER, ValidationSchemes.NON_NEGATIVE]}
                        input_state_control={props.inputStateControl}
                        type={TextfieldInputTypes.NUMBER}
                    />
                </Col>
                <Col md={6}>
                    <RBSDropdown
                        label={getLabelTextMessage(LabelTextMessageTypes.REFERENCE_VARIABLE_ID, props.user_language)}
                        input_key="reference_variable_id"
                        prefill={prefill("reference_variable_id")}
                        items={[{ label: "---", value: "" }, ...(props.variables ? props.variables.map(v => ({ label: v.name, value: v.id })) : [])]}
                        input_state_control={props.inputStateControl}
                        validation_schemes={[]}
                    />
                </Col>
            </Row>
            <Row>
                <Col md={12}>
                    <RBSTextField
                        label={getLabelTextMessage(LabelTextMessageTypes.DESCRIPTION, props.user_language)}
                        input_key="description"
                        prefill={prefill("description")}
                        validation_schemes={[ValidationSchemes.REQUIRED]}
                        input_state_control={props.inputStateControl}
                        type={TextfieldInputTypes.TEXT}
                        multiline={true}
                        style={{ minHeight: 300 }}
                    />
                </Col>
            </Row>
        </RBSModal>
    );
};
